// src/components/RequireRole.jsx
import React from "react";
import { Navigate } from "react-router-dom";
import useUserProfile from "../hooks/useUserProfile";
import useDeliveryRole from "../hooks/useDeliveryRole";
import usePackerRole from "../hooks/usePackerRole";

export default function RequireRole({ allowed = [], children, fallback = "/", coopRole, isAdmin, isFinancial }) {
  const { loading, profile } = useUserProfile();
  
  // Check if any of the allowed roles are delivery-related
  const hasDeliveryRoles = allowed.some(role => ['delivery', 'rider'].includes(role));
  
  // Check if any of the allowed roles are website user roles
  const hasWebsiteUserRoles = allowed.some(role => ['packer', 'delivery', 'rider'].includes(role));
  
  // For delivery routes, use delivery role system; for others, use cooperative member roles
  const { isDelivery: isDeliveryUser, isRider, isDeliveryPerson } = useDeliveryRole();
  
  // For website user roles, use the appropriate hooks
  const { isPacker, loading: packerLoading } = usePackerRole();
  


  // While loading, show loading indicator
  if (loading || packerLoading) {
    
    return (
      <div className="mx-auto max-w-3xl p-6">
        <div className="card p-6">
          <p className="text-ink/70">Loading permissions...</p>
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-2 text-xs text-ink/50">
              <p>Profile loading: {loading ? 'Yes' : 'No'}</p>
              <p>Packer loading: {packerLoading ? 'Yes' : 'No'}</p>
              <p>Allowed roles: {allowed.join(', ') || 'None'}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // For cooperative routes, check if we have the role data
  // But allow admin access even if coopRole is null (admin might be in users collection)
  // Also allow access if user has website user roles (packer, delivery, rider)
  if (!hasWebsiteUserRoles && !coopRole && !isAdmin) {
    // No role found - user doesn't have access
    return <Navigate to={fallback} replace />;
  }

  // Optional: if user is suspended, treat as not allowed
  if (profile?.suspended === true) {
    return <Navigate to="/" replace />;
  }

  // If no specific roles required, allow; otherwise check based on route type
  let ok = false;
  
  if (allowed.length === 0) {
    ok = true; // No roles required
  } else if (hasWebsiteUserRoles) {
    // For website user routes, check website user roles AND cooperative member roles
    ok = allowed.some(role => {
      if (role === 'packer') return isPacker();
      if (role === 'delivery') return isDeliveryUser;
      if (role === 'rider') return isRider;
      // Check both website user role and cooperative member role
      if (role === 'cashier') return coopRole === 'cashier';
      if (role === 'treasurer') return coopRole === 'treasurer';
      if (role === 'manager') return coopRole === 'manager';
      if (role === 'general manager') return coopRole === 'general manager';
      if (role === 'cooperative_store_manager') return coopRole === 'cooperative_store_manager';
      if (role === 'member_store_manager') return coopRole === 'member_store_manager';
      if (role === 'establishment_store_manager') return coopRole === 'establishment_store_manager';
      return false;
    });
    
    // Also allow admin access to any route (but only for actual cooperative admins)
    if (isAdmin && coopRole === 'admin') {
      ok = true;
    }
  } else {
    // For cooperative routes, use direct role comparison instead of hasAnyRole
    // Also check if user is admin (admin can access all routes, but only actual cooperative admins)
    ok = allowed.includes(coopRole) || (isAdmin && coopRole === 'admin');
  }
  

  
  return ok ? children : <Navigate to={fallback} replace />;
}
